<!-- eslint-disable vue/no-v-html -->
<template>
  <v-tour
    name="main"
    :options="options"
    :steps="modifiedSteps"
    :callbacks="callbacks"
    :tour-active="tourActive"
  >
    <template slot-scope="tour">
      <v-step
        v-if="tour.steps[tour.currentStep]"
        :key="tour.currentStep"
        :step="tour.steps[tour.currentStep]"
        v-bind="tour"
        class="v-card v-sheet white"
      >
        <div
          slot="header"
          class="v-card__title"
          v-html="tour.steps[tour.currentStep].header"
        ></div>

        <div slot="content" class="v-card__text pb-2">
          <v-divider class="mb-4" />
          <component
            :is="tour.steps[tour.currentStep].content"
            v-if="_isComponent(tour.steps[tour.currentStep].content)"
          />
          <template
            v-else
            v-html="tour.steps[tour.currentStep].content"
          ></template>
        </div>
        <div slot="actions" class="v-card__actions d-flex justify-center">
          <v-btn
            v-if="!tour.isLast"
            small
            outlined
            color="primary"
            @click="tour.skip"
          >{{tour.labels.buttonSkip}}</v-btn>
          <v-btn
            v-if="!tour.isFirst"
            small
            outlined
            color="primary"
            @click="tour.previousStep"
          >{{tour.labels.buttonPrevious}}</v-btn>
          <v-btn
            v-if="!tour.isLast"
            small
            outlined
            color="primary"
            @click="tour.nextStep"
          >{{tour.labels.buttonNext}}</v-btn>
          <v-btn
            v-if="tour.isLast"
            small
            outlined
            color="primary"
            @click="tour.finish"
          >{{tour.labels.buttonStop}}</v-btn>
        </div>
      </v-step>
    </template>
  </v-tour>
</template>

<script>
  import {StepRoomCurrent} from './StepRoomCurrent';
  import {StepPeerSelf} from './StepPeerSelf';
  import {StepPeerDescription} from './StepPeerDescription';
  import {StepPeerSend} from './StepPeerSend';
  import {StepConnection} from './StepConnection';
  import {StepRooms} from './StepRooms';
  import {StepRoomChange} from './StepRoomChange';
  import {StepRoomInvite} from './StepRoomInvite';
  import {StepRoomFiles} from './StepRoomFiles';
  import eventBus from '../../assets/js/event-bus';


  export default {
    name: 'Tour',

    data() {
      return {
        options: {
          labels: {
            buttonSkip: 'Hilfe überspringen',
            buttonPrevious: 'Zurück',
            buttonNext: 'Weiter',
            buttonStop: 'Fertig'
          }
        },
        steps: [
          StepRoomCurrent,
          StepPeerSelf,
          StepPeerDescription,
          StepPeerSend,
          StepConnection,
          StepRooms,
          StepRoomChange,
          StepRoomInvite,
          StepRoomFiles
        ],
        callbacks: {
          onStart: () => this._updateTour({step: 0}),
          onPreviousStep: (currentStep) => this._updateTour({step: currentStep - 1}),
          onNextStep: (currentStep) => this._updateTour({step: currentStep + 1}),
          onSkip: () => this._onDone(),
          onStop: () => this._onDone(),
          onFinish: () => this._onDone()
        },
        routeChangeAllowed: false
      };
    },

    computed: {
      tourActive() {
        return !!this.$store.getters['share/getTour'];
      },

      modifiedSteps() {
        return this.steps.map((config, step) => ({
          ...config,
          before: async (type) => {
            this.routeChangeAllowed = true;

            const previousStep = type === 'previous' ? step + 1 : step - 1;
            // Call after hook of previous step.
            await this.steps[previousStep]?.after?.();
            // Call actual before hook of current step.
            await config.before?.(type);

            this.routeChangeAllowed = false;
          }
        }));
      },

      user() {
        return this.$store.getters['session/getUser'];
      }
    },

    watch: {
      user(user) {
        if (!user?.loggedIn) {
          this.$tours.main.stop();
        }
      }
    },

    mounted() {
      // Append element to .v-mail__wrap otherwise the steps won't be visible within dialogs.
      document.querySelector('.v-main__wrap').appendChild(this.$el);

      eventBus.$on('tour-start', this.start);

      // Check if tour is active.
      this._unwatchBeforeEach = this.$router.beforeEach((to, from, next) => {
        if (this._getTour() && !this.routeChangeAllowed) {
          this.$tours.main.stop();
        }
        next();
      });
    },

    destroyed() {
      this._setTour(null);
      eventBus.$off('tour-start', this.start);
      this._unwatchBeforeEach?.();
    },

    methods: {
      start() {
        this.startPath = this.$route.fullPath; // Store current path.
        this.$tours.main.start();
      },

      _setTour(value) {
        this.$store.commit('share/setTour', value);
      },

      _updateTour(value) {
        this.$store.commit('share/updateTour', value);
      },

      _getTour() {
        return this.$store.getters['share/getTour'];
      },

      _isComponent(value) {
        return typeof value === 'object' && typeof value.render === 'function';
      },

      _onDone() {
        const step = this._getTour()?.step;
        this.steps[step]?.after?.();
        this._setTour(null);
        this.$router.replace(this.startPath);
      }
    }
  };
</script>

<style lang="scss" scoped>
  .v-tour[tour-active] {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.3);
  }

  .v-step {
    width: 420px;
    max-width: calc(100vw - 20px) !important;

    .v-card__title {
      font-size: 16px !important;
      line-height: 16px;
    }

    &::v-deep {
      .v-step__arrow,
      .v-step__arrow::before {
        position: absolute;
        width: 10px;
        height: 10px;
        text-align: center;
      }

      .v-step__arrow::before {
        visibility: visible;
        content: "";
        margin-left: -5px;
        background: #fff;
        /* stylelint-disable length-zero-no-unit, number-no-trailing-zeros */
        box-shadow:
          0px 3px 1px -2px rgba(0, 0, 0, 0.20),
          0px 2px 2px 0px rgba(0, 0, 0, 0.14),
          0px 1px 5px 0px rgba(0, 0, 0, 0.12);
        /* stylelint-enable */
      }
    }

    &[data-popper-placement^="bottom"]::v-deep > .v-step__arrow {
      top: -5px;

      &::before {
        transform: rotate(135deg);
      }
    }

    &[data-popper-placement^="top"]::v-deep > .v-step__arrow {
      bottom: -5px;

      &::before {
        transform: rotate(45deg);
      }
    }

    .v-card__text::v-deep p:last-child {
      margin-bottom: 0;
    }
  }
</style>
